import {
  Box,
  Button,
  CircularProgress,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  MenuItem,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { useGA4React } from 'ga-4-react';
import React, { Fragment, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { useSelector } from 'react-redux';

import { yupResolver } from '@hookform/resolvers/yup';

import InfoIcon from '@mui/icons-material/Info';
import { makeStyles } from '@mui/styles';
import moment from 'moment-business-days';
import * as yup from 'yup';
import { numberToCurrency, objectToArray } from '../../libs/helpers';
import PrimaryButton from '../ui/buttons/primaryButton';
import Checkbox from '../ui/forms/fields/checkbox';
import GreatDatePicker from '../ui/forms/fields/datePicker';
import Input from '../ui/forms/fields/input';
import CustomRadioGroup from '../ui/forms/fields/radioGroup';
import Select from '../ui/forms/fields/select';
import { CountryList, IndustriesList, SourceOfFundsList } from '../../libs/staticData';
import SearchDropdown from '../ui/forms/fields/searchDropDoown';
import { Report } from '../../libs/loggers';

const useStyles = makeStyles((theme) => ({
  tableCell: {
    [theme.breakpoints.down('md')]: {
      padding: 0,
    },
  },
}));

function BankingDetails({
  extendedForm,
  data,
  submitionData,
  onNext,
  onBack,
  stepSlug,
  openSave,
  onSaveAndExit,
  isRaHelp,
  isBciApplication,
  isApexRihfOrQihf,
}) {
  let ga4React = useGA4React();

  const page = window.location.pathname.replace(/^\/|\/$/g, '');
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('sm'));
  const submitionId = useSelector((state) => state.submition.submition?.submitionId);
  const formStepper = useSelector((state) => state.formStepper.value);

  const loading = useSelector((state) => state.submition.submition?.loading);
  const userId = useSelector((state) => state.submition.data?.userId);

  const bankCodeMapping = useMemo(
    () => ({
      'Absa Bank Ltd': '632 005',
      'African Bank Ltd': '430 000',
      'Bidvest Bank Limited': '462 005',
      'Capitec Bank Ltd': '470 010',
      'First National Bank': '250 655',
      'FirstRand Bank Ltd': '250 655',
      'Investec Bank Ltd': '580 105',
      'Nedbank Group Limited': '198 765',
      'Rand Merchant Bank': '254 655',
      'Sasfin Bank Ltd': '683 000',
      'Standard Bank of SA Ltd': '051 001',
      Other: '',
    }),
    []
  );

  const classes = useStyles();
  const BankingDetailsFormValidationSchema = (
    data,
    extendedForm,
    isBciApplication,
    isApexRihfOrQihf
  ) => {
    const validationSchema = yup.object().shape({
      stepSlug: yup.string().required(),
      bankAccountHolderName: yup.string().required('Please provide your account holder name.'),
      bankName: yup.string().required('Please provide the bank name.'),
      bankBranch: yup.string().required('Please give the branch name of your bank details.'),
      otherBankName: yup.string().when('bankName', {
        is: 'Other',
        then: yup.string().required('Please give the other bank name of your bank details.'),
        otherwise: yup.string(),
      }),
      bankCode: yup.string().required('What your bank branch code?'),
      bankAccountNo: yup.string().required('Please type in your bank account number.'),
      bankAccountType: yup
        .string()
        .required('What is the bank account type? (Current|Savings|Cheque)'),
      mopLumpsumMethod: yup
        .string()
        .test('hasLumbsumTest', 'Please select at least one Lump sum method.', function (val) {
          let isValid = false;
          const hasLump = hasLumpsum(data);

          if (hasLump) {
            if (val) isValid = true;
          } else {
            isValid = true;
          }

          return isValid;
        }),

      mopElectronicCollectionDate: yup.string().when('mopLumpsumMethod', {
        is: (mopLumpsumMethod) => mopLumpsumMethod === 'ElectronicCollection',
        then: yup
          .string()
          .test(
            'notWeekend',
            'Date cannot be on a weekend, please select a weekday',
            function (val) {
              const isValid = moment(val).isoWeekday() !== 6 && moment(val).isoWeekday() !== 7;
              return isValid;
            }
          )
          .test('pastDate', 'Please select a date at least 2 days in the future.', function (val) {
            const isValid = moment().isBefore(moment(val));
            return isValid;
          }),

        otherwise: yup.string(),
      }),

      mopRegularDebitOrder: yup.bool(),
      mopRegularDebitOrderDate: yup.string().when('mopRegularDebitOrder', {
        is: (mopRegularDebitOrder) => mopRegularDebitOrder,
        then: yup.string().required('Please select a debit order date.'),
        otherwise: yup.string(),
      }),

      mopRegularDebitOrderAnnualEscalation: yup.number().when('mopRegularDebitOrder', {
        is: (mopRegularDebitOrder) => mopRegularDebitOrder,
        then: yup
          .number('Please select an Annual Escalation (%).')
          .required('Please select an Annual Escalation (%).'),
        otherwise: yup.number(),
      }),

      mopAlternativeBankingDetails: yup.bool().default(false).defined(),

      debitOrderBanking_AccountHolder: yup.string().when('mopAlternativeBankingDetails', {
        is: (mopAlternativeBankingDetails) =>
          mopAlternativeBankingDetails && (!isBciApplication || !isApexRihfOrQihf),
        then: yup.string().required('Please provide the alternative account holder name.'),
        otherwise: yup.string(),
      }),
      debitOrderBanking_Bank: yup.string().when('mopAlternativeBankingDetails', {
        is: (mopAlternativeBankingDetails) => mopAlternativeBankingDetails,
        then: yup.string().required('Please provide the alternative bank name.'),
        otherwise: yup.string(),
      }),
      debitOrderBanking_BranchName: yup.string().when('mopAlternativeBankingDetails', {
        is: (mopAlternativeBankingDetails) => mopAlternativeBankingDetails,
        then: yup.string().required('Please provide the alternative bank branch name.'),
        otherwise: yup.string(),
      }),
      otherDebitOrderBanking_BankName: yup.string().when('debitOrderBanking_Bank', {
        is: 'Other',
        then: yup.string().required('Please provide the other alternative bank name.'),
        otherwise: yup.string(),
      }),
      debitOrderBanking_BranchCode: yup.string().when('mopAlternativeBankingDetails', {
        is: (mopAlternativeBankingDetails) => mopAlternativeBankingDetails,
        then: yup.string().required('Please provide the alternative bank brach code.'),
        otherwise: yup.string(),
      }),
      debitOrderBanking_AccountNumber: yup.string().when('mopAlternativeBankingDetails', {
        is: (mopAlternativeBankingDetails) => mopAlternativeBankingDetails,
        then: yup.string().required('Please provide us with the alternative account number.'),
        otherwise: yup.string(),
      }),
      debitOrderBanking_AccountType: yup.string().when('mopAlternativeBankingDetails', {
        is: (mopAlternativeBankingDetails) => mopAlternativeBankingDetails,
        then: yup
          .string()
          .required('What type of alternative bank account is it? (Current|Savings|Cheque).'),
        otherwise: yup.string(),
      }),
      fullNameThirdParty: yup.string().when('mopAlternativeBankingDetails', {
        is: true,
        then: (schema) => schema.required('Full name is required.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      dobThirdParty: yup.mixed().when('mopAlternativeBankingDetails', {
        is: (val) => !!val,
        then: () =>
          yup
            .date()
            .nullable()
            .transform((curr, orig) => (orig === '' ? null : curr))
            .typeError('Date of birth is required.')
            .max(new Date(), 'Date of birth cannot be in the future.')
            .required('Date of birth is required.'),
        otherwise: () =>
          yup
            .date()
            .nullable()
            .transform((curr, orig) => (orig === '' ? null : curr))
            .typeError('Date of birth must be a valid date.')
            .max(new Date(), 'Date of birth cannot be in the future.')
            .notRequired(),
      }),

      idRegThirdParty: yup.string().when('mopAlternativeBankingDetails', {
        is: true,
        then: (schema) => schema.required('Identity number is required.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      emailThirdParty: yup
        .string()
        .email('Enter a valid email address.')
        .when('mopAlternativeBankingDetails', {
          is: true,
          then: (schema) => schema.required('Email address is required.'),
          otherwise: (schema) => schema.notRequired(),
        }),

      cellThirdParty: yup.string().when('mopAlternativeBankingDetails', {
        is: true,
        then: (schema) => schema.required('Mobile number is required.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      thirdPartySOF: yup.string().when('mopAlternativeBankingDetails', {
        is: true,
        then: (schema) => schema.required('Source of funds is required.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      thirdPartySOFOther: yup.string().when(['mopAlternativeBankingDetails', 'thirdPartySOF'], {
        is: (mopAlternativeBankingDetails, thirdPartySOF) =>
          mopAlternativeBankingDetails && thirdPartySOF === 'other',
        then: (schema) => schema.required('Please specify the source of funds.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      thirdPartyCountry: yup.string().when('mopAlternativeBankingDetails', {
        is: true,
        then: (schema) => schema.required('Country is required.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      thirdPartyAddress1: yup.string().when('mopAlternativeBankingDetails', {
        is: true,
        then: (schema) => schema.required('Address Line 1 is required.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      thirdPartyPostalCode: yup.string().when('mopAlternativeBankingDetails', {
        is: true,
        then: (schema) => schema.required('Postal Code is required.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      thirdPartyOccupation: yup.string().when('mopAlternativeBankingDetails', {
        is: true,
        then: (schema) => schema.required('Occupation is required.'),
        otherwise: (schema) => schema.notRequired(),
      }),

      thirdPartyOccupationOther: yup
        .string()
        .when(['mopAlternativeBankingDetails', 'thirdPartyOccupation'], {
          is: (mopAlternativeBankingDetails, occupation) =>
            mopAlternativeBankingDetails && occupation === 'Other',
          then: (schema) => schema.required('Please specify the occupation.'),
          otherwise: (schema) => schema.notRequired(),
        }),
    });

    return validationSchema;
  };

  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    watch,
    trigger,
    getValues,
    setValue,
  } = useForm({
    mode: 'onBlur',
    defaultValues: {
      mopAlternativeBankingDetails: false,
    },
    resolver: yupResolver(
      BankingDetailsFormValidationSchema(data, extendedForm, isBciApplication, isApexRihfOrQihf)
    ),
  });

  useEffect(() => {
    if (window.innerWidth <= 768) {
      window.scrollTo(0, 0);
    }
  }, []);

  useEffect(() => {
    if (submitionData) {
      Object.entries(submitionData).forEach(([key, value]) => {
        setValue(key, value, { validate: true });
      });
    }
  }, [submitionData, setValue]);

  const watchDebitOrderBanking_BranchName = watch('debitOrderBanking_BranchName');
  const watchBankBranch = watch('bankBranch');
  const watchEC = watch('mopElectronicCollectionDate');
  const watchDebitOrderBanking_Bank = watch('debitOrderBanking_Bank');
  const watchBankName = watch('bankName');
  const watchDebitOrderBanking_BranchCode = watch('debitOrderBanking_BranchCode');

  useEffect(() => {
    setValue('stepSlug', stepSlug, { shouldValidate: true });
  }, [stepSlug, setValue]);

  useEffect(() => {
    const selectedBankCode = bankCodeMapping[watchBankName];
    setValue('bankCode', selectedBankCode);
  }, [watchBankName, setValue, bankCodeMapping]);

  useEffect(() => {
    const selectedBankCode = bankCodeMapping[watchDebitOrderBanking_Bank];
    setValue('debitOrderBanking_BranchCode', selectedBankCode);
  }, [watchDebitOrderBanking_Bank, setValue, bankCodeMapping]);

  useEffect(() => {
    if (watchBankName && watchBankName in bankCodeMapping) {
      setValue('bankBranch', 'Universal Branch Code Used');
    }
  }, [watchBankName, setValue, bankCodeMapping]);

  useEffect(() => {
    if (watchDebitOrderBanking_Bank && watchDebitOrderBanking_Bank in bankCodeMapping) {
      setValue('debitOrderBanking_BranchName', 'Universal Branch Code Used');
    }
  }, [watchDebitOrderBanking_Bank, setValue, bankCodeMapping]);

  useEffect(() => {
    trigger('mopElectronicCollectionDate');
  }, [trigger, watchEC]);

  useEffect(() => {
    const eventInfo = {
      action: 'view',
      category: 'step4_formstep_view',
      description: 'viewed a form step in step 4',
      value: {
        currentPage: page,
        stepSlug,
        activeFormStep: formStepper?.step,
      },
    };

    Report(eventInfo, userId, submitionId);
  }, []);

  const bankName = watch('bankName');
  const debitOrderBanking_Bank = watch('debitOrderBanking_Bank');

  const NavButtons = (props) => (
    <>
      <Box display="flex" justifyContent={'flex-end'}>
        <PrimaryButton disabled={props.isFirstStep} onClick={() => onBack()} sx={{ mt: 1, mr: 1 }}>
          Back
        </PrimaryButton>
        <PrimaryButton
          onClick={() => handleNext()}
          disabled={loading}
          sx={{
            mt: 1,
            mr: 1,
            '&.Mui-disabled': {
              background: '#CC6323',
              color: '#fff',
            },
          }}
        >
          {userId ? 'Save & Continue' : 'Continue'}{' '}
          {loading && <CircularProgress sx={{ ml: 1 }} size={isMobile ? 15 : 20} color="inherit" />}
        </PrimaryButton>
        <PrimaryButton variant="outlined" onClick={() => handleSaveAndExit(userId)} sx={{ mt: 1 }}>
          {'Save & Exit'}
        </PrimaryButton>
      </Box>
      {!userId && (
        <Box display="flex" justifyContent={'flex-end'} marginTop={1}>
          <Typography variant="body1">
            {' '}
            Your progress is not being saved. Click{' '}
            <Link
              sx={{ cursor: 'pointer' }}
              variant="body1"
              underline="always"
              onClick={() => {
                handleOpenSave();
              }}
            >
              here
            </Link>{' '}
            to save.
          </Typography>
        </Box>
      )}
    </>
  );

  const handleNext = async () => {
    ga4React.event('complete_form_step', `${stepSlug}`, 'investement_submition');

    console.log('Validation errors:', errors);
    setValue('completeProcess', false, { shouldValidate: true });
    handleSubmit(onSubmit)();
  };

  const handleSaveAndExit = (userId) => {
    const data = getValues();
    const newSubmition = { ...submitionData, ...data };
    onSaveAndExit(newSubmition, userId);
  };

  const handleOpenSave = () => {
    const data = getValues();
    const newSubmition = { ...submitionData, ...data };
    openSave(newSubmition);
  };

  const onSubmit = (data) => {
    const newSubmition = { ...submitionData, ...data };
    onNext(newSubmition);
  };

  const hasDebitOrders = () => {
    if (data && data.funds) {
      for (var i = 0; i < data.funds.length; i++) {
        const fund = data.funds[i];
        if (fund.monthly && fund.monthly !== 0) {
          return true;
        }
      }
    }

    return false;
  };

  const hasLumpsum = () => {
    if (data && data.funds) {
      for (var i = 0; i < data.funds.length; i++) {
        const fund = data.funds[i];
        if (fund.lumpsum && fund.lumpsum !== 0) {
          return true;
        }
      }
    }
    return false;
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={12} md={12}>
        <Typography variant="h5" color="primary" sx={{ margin: '8.5px 0' }}>
          Investors Bank Details
        </Typography>
      </Grid>
      <Grid item xs={12} md={6}>
        <Input
          required
          label="Account Holder Name"
          {...register('bankAccountHolderName')}
          error={!!errors.bankAccountHolderName}
          helperText={errors?.bankAccountHolderName?.message}
        />

        <Select
          label="Bank Name"
          id="bankName"
          name="bankName"
          {...register('bankName')}
          error={!!errors?.bankName}
          required
          helperText={errors?.bankName?.message}
          control={control}
        >
          <MenuItem value="Absa Bank Ltd">Absa Bank Ltd</MenuItem>
          <MenuItem value="African Bank Ltd">African Bank Ltd</MenuItem>
          <MenuItem value="Bidvest Bank Limited">Bidvest Bank Limited</MenuItem>
          <MenuItem value="Capitec Bank Ltd">Capitec Bank Ltd</MenuItem>
          <MenuItem value="First National Bank">First National Bank</MenuItem>
          <MenuItem value="FirstRand Bank Ltd">FirstRand Bank Ltd</MenuItem>
          <MenuItem value="Investec Bank Ltd">Investec Bank Ltd</MenuItem>
          <MenuItem value="Nedbank Group Limited">Nedbank Group Limited</MenuItem>
          <MenuItem value="Rand Merchant Bank">Rand Merchant Bank</MenuItem>
          <MenuItem value="Sasfin Bank Ltd">Sasfin Bank Ltd</MenuItem>
          <MenuItem value="Standard Bank of SA Ltd">Standard Bank of SA Ltd</MenuItem>
          <MenuItem value="Other">Other</MenuItem>
        </Select>
        {bankName === 'Other' && (
          <Input
            label="Other Bank Name"
            {...register('otherBankName')}
            error={!!errors?.otherBankName}
            required
            helperText={errors?.otherBankName?.message}
          />
        )}
        <Input
          required
          label="Bank Account Number"
          {...register('bankAccountNo')}
          error={!!errors.bankAccountNo}
          helperText={errors?.bankAccountNo?.message}
        />
        <Input
          label="Swift"
          {...register('swift')}
          error={!!errors?.swift}
          helperText={errors?.swift?.message}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        {isBciApplication || isApexRihfOrQihf ? (
          <Select
            required
            name="bankAccountType"
            label="Bank Account Type"
            control={control}
            error={!!errors.bankAccountType}
            helperText={errors?.bankAccountType?.message}
          >
            <MenuItem value="Savings">Savings</MenuItem>
            <MenuItem value="Current">Current</MenuItem>
            <MenuItem value="Transmission">Transmission</MenuItem>
          </Select>
        ) : (
          <Select
            required
            name="bankAccountType"
            label="Bank Account Type"
            control={control}
            error={!!errors.bankAccountType}
            helperText={errors?.bankAccountType?.message}
          >
            <MenuItem value="Savings">Savings</MenuItem>
            <MenuItem value="Cheque">Cheque</MenuItem>
          </Select>
        )}
        <Input
          required
          label="Bank Branch"
          {...register('bankBranch')}
          error={!!errors.bankBranch}
          helperText={errors?.bankBranch?.message}
          InputLabelProps={{
            shrink: !!(watchBankBranch && watchBankBranch.length > 0),
          }}
        />

        <Input
          label="Bank Code"
          {...register('bankCode')}
          error={!!errors.bankCode}
          helperText={errors?.bankCode?.message}
          InputLabelProps={{
            shrink: !!(watchBankName && bankCodeMapping[watchBankName]?.length > 0),
          }}
        />
        <Input
          label="IBAN"
          {...register('IBAN')}
          error={!!errors?.IBAN}
          helperText={errors?.IBAN?.message}
        />
      </Grid>
      <Grid item xs={12} md={6}>
        <Typography variant="h5" color="primary" sx={{ margin: '8.5px 0' }}>
          Method of Payment
        </Typography>
      </Grid>
      <Grid item xs={12}>
        {data.funds && (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell className={classes.tableCell}>Fund</TableCell>
                <TableCell className={classes.tableCell}>Lump Sum</TableCell>
                <TableCell className={classes.tableCell}>Debit Order</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.funds.map((fund, index) => (
                <TableRow key={index}>
                  <TableCell>{fund.fund.name}</TableCell>
                  <TableCell>{numberToCurrency(fund.lumpsum || 0)}</TableCell>
                  <TableCell>
                    {hasDebitOrders() ? (
                      <>{numberToCurrency(fund.monthly || 0)}</>
                    ) : (
                      <Typography>N/A</Typography>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Grid>
      {hasLumpsum() && (
        <Grid item xs={12} md={6}>
          <Typography variant={'h5'} color={'primary'}>
            Lump Sum
          </Typography>
          <CustomRadioGroup
            control={control}
            aria-label="mopLumpsumMethod"
            name="mopLumpsumMethod"
            error={!!errors?.mopLumpsumMethod}
            helperText={errors?.mopLumpsumMethod?.message}
            xs={{ margin: 1 }}
          >
            <FormControlLabel value={'EFT'} control={<Radio />} color="primary" label={'EFT'} />
            {!extendedForm && (
              <FormControlLabel
                value={'ElectronicCollection'}
                control={<Radio />}
                color="primary"
                label={'Electronic Collection'}
              />
            )}
          </CustomRadioGroup>

          {watch('mopLumpsumMethod') === 'EFT' && (
            <Typography>
              If you wish to make an EFT payment, we will provide you with our bank details and your
              client account number once your account is opened.
            </Typography>
          )}
          {watch('mopLumpsumMethod') === 'ElectronicCollection' && (
            <GreatDatePicker
              name={'mopElectronicCollectionDate'}
              control={control}
              label="Electronic Collection Date"
              minDate={moment().businessAdd(2, 'days')}
              defaultValue={moment().businessAdd(2, 'days')}
              error={!!errors.mopElectronicCollectionDate}
              helperText={
                'To allow time for collection instructions to be processed the earliest collection date will be 2 business days from today.'
              }
            />
          )}
        </Grid>
      )}
      {hasDebitOrders() && (
        <Grid item xs={12} md={6}>
          <Typography variant={'h5'} color={'primary'}>
            Debit Order
          </Typography>

          <Checkbox
            name={'mopRegularDebitOrder'}
            label="Regular Debit Order"
            control={control}
            value={true}
          />
          <br />
          <FormHelperText>
            Funds are collected from the investor’s bank account on the 1st working day of each
            month.
          </FormHelperText>
          <FormHelperText>
            Please note cut-off for debit order instructions is 6 business days before the 1st of
            the new month.
          </FormHelperText>
          {watch('mopRegularDebitOrder') && (
            <Fragment>
              <GreatDatePicker
                name={'mopRegularDebitOrderDate'}
                control={control}
                label="Debit Order Commencement Month"
                minDate={moment().add(1, 'month').businessAdd(6, 'days')}
                maxDate={moment().add(2, 'years')}
                views={['year', 'month']}
                inputFormat="MMMM YYYY"
                defaultCalendarMonth={moment().add(1, 'month').businessAdd(6, 'days')}
                openTo="month"
                error={!!errors.mopRegularDebitOrderDate}
              />
              <Select
                name="mopRegularDebitOrderAnnualEscalation"
                label="Annual Escalation (%)"
                control={control}
                error={!!errors.mopRegularDebitOrderAnnualEscalation}
                defaultValue={'0'}
              >
                <MenuItem value="0">0%</MenuItem>
                <MenuItem value="5">5%</MenuItem>
                <MenuItem value="6">6%</MenuItem>
                <MenuItem value="10">10%</MenuItem>
                <MenuItem value="15">15%</MenuItem>
                <MenuItem value="20">20%</MenuItem>
              </Select>
              <Tooltip
                leaveTouchDelay={2500}
                enterTouchDelay={0}
                title={
                  <Typography>
                    Your annual debit order escalation % can be cancelled or amended at any point by
                    way of an email instruction to our client services.
                  </Typography>
                }
              >
                <Button startIcon={<InfoIcon color="primary" size={'small'} />}>
                  <FormHelperText sx={{ textTransform: 'capitalize' }}>
                    {' '}
                    Tell me more.
                  </FormHelperText>
                </Button>
              </Tooltip>
            </Fragment>
          )}
        </Grid>
      )}
      {!extendedForm && (
        <Grid item xs={12}>
          <Checkbox
            name={'mopAlternativeBankingDetails'}
            label={'Use Third Party Banking Details for Debit Order/Electronic Collection'}
            control={control}
          />
        </Grid>
      )}
      {watch('mopAlternativeBankingDetails') ? (
        <>
          {/* Third Party Personal Information Section */}
          <Typography variant="h5" color="primary" sx={{ margin: '8.5px 0' }}>
            Third Party Personal Information
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              <Input
                label="First Name(s) and Surname"
                {...register('fullNameThirdParty')}
                error={!!errors?.fullNameThirdParty}
                helperText={errors?.fullNameThirdParty?.message}
                required
              />
              <GreatDatePicker
                name={'dobThirdParty'}
                control={control}
                label="Date of Birth"
                error={!!errors.dobThirdParty}
                helperText={errors?.dobThirdParty?.message}
                maxDate={moment()}
                openTo={'year'}
              />

              <Input
                label="Identity Number"
                {...register('idRegThirdParty')}
                error={!!errors?.idRegThirdParty}
                helperText={errors?.idRegThirdParty?.message}
                required
              />
              <Input
                label="Email Address"
                {...register('emailThirdParty')}
                error={!!errors?.emailThirdParty}
                helperText={errors?.emailThirdParty?.message}
                required
              />
              <Input
                label="Mobile Number"
                {...register('cellThirdParty')}
                error={!!errors?.cellThirdParty}
                helperText={errors?.cellThirdParty?.message}
                required
              />
              <Select
                name="thirdPartySOF"
                label="Source of Funds"
                control={control}
                error={!!errors.thirdPartySOF}
                helperText={errors?.thirdPartySOF?.message}
                required
              >
                {SourceOfFundsList.map((item) => {
                  if (
                    (item.rules && item.rules.vehicle === data.vehicle) ||
                    (item.rules && item.rules.formSlug === 'ra_invest_help')
                  ) {
                    return (
                      <MenuItem key={item.key} value={item.value}>
                        {item.value}
                      </MenuItem>
                    );
                  } else if (!item.rules) {
                    return (
                      <MenuItem key={item.key} value={item.value}>
                        {item.value}
                      </MenuItem>
                    );
                  } else {
                    return null;
                  }
                })}
                <MenuItem value="other">Other</MenuItem>
              </Select>
              {watch('thirdPartySOF') === 'other' ? (
                <Input
                  label="Please specify Other source"
                  {...register('thirdPartySOFOther')}
                  error={!!errors.thirdPartySOFOther}
                  helperText={errors?.thirdPartySOFOther?.message}
                  required
                />
              ) : null}
            </Grid>

            <Grid item xs={12} md={6}>
              <SearchDropdown
                required
                name="thirdPartyCountry"
                label="Country"
                control={control}
                error={!!errors.thirdPartyCountry}
                helperText={errors?.thirdPartyCountry?.message}
                options={CountryList}
              />
              <Input
                label="Address Line 1"
                {...register('thirdPartyAddress1')}
                error={!!errors?.thirdPartyAddress}
                helperText={errors?.thirdPartyAddress?.message}
              />
              <Input
                label="Address Line 2"
                {...register('thirdPartyAddress2')}
                error={!!errors?.thirdPartyAddress}
                helperText={errors?.thirdPartyAddress?.message}
              />
              <Input
                label="Address Line 3"
                {...register('thirdPartyAddress2')}
                error={!!errors?.thirdPartyAddress}
                helperText={errors?.thirdPartyAddress?.message}
              />

              <Input
                label="Postal Code"
                {...register('thirdPartyPostalCode')}
                error={!!errors?.thirdPartyPostalCode}
                helperText={errors?.thirdPartyPostalCode?.message}
                required
              />
              <Select
                required
                name="thirdPartyOccupation"
                label={'Occupation'}
                control={control}
                error={!!errors.thirdPartyOccupation}
                helperText={errors?.thirdPartyOccupation?.message}
              >
                {IndustriesList.map((item) => (
                  <MenuItem key={item.key} value={item.value}>
                    {item.value}
                  </MenuItem>
                ))}
                <MenuItem value={'Other'}>Other</MenuItem>
              </Select>

              {watch('thirdPartyOccupation') === 'Other' ? (
                <Input
                  required
                  label={'Please specify Other Occupation'}
                  {...register('thirdPartyOccupationOther')}
                  error={!!errors.thirdPartyOccupationOther}
                  helperText={errors?.thirdPartyOccupationOther?.message}
                />
              ) : null}
            </Grid>
          </Grid>

          {/* Third Party Bank Details Section */}
          <Typography variant="h5" color="primary" sx={{ margin: '8.5px 0' }}>
            Third Party Bank Details
          </Typography>

          <Grid container spacing={1}>
            <Grid item xs={12} md={6}>
              {!isBciApplication && !isApexRihfOrQihf && (
                <Input
                  label="Account Holder Name"
                  {...register('debitOrderBanking_AccountHolder')}
                  error={!!errors?.debitOrderBanking_AccountHolder}
                  helperText={errors?.debitOrderBanking_AccountHolder?.message}
                  required
                />
              )}

              <Select
                label="Bank Name"
                {...register('debitOrderBanking_Bank')}
                error={!!errors?.debitOrderBanking_Bank}
                required
                name="debitOrderBanking_Bank"
                helperText={errors?.debitOrderBanking_Bank?.message}
                control={control}
              >
                <MenuItem value="Absa Bank Ltd">Absa Bank Ltd</MenuItem>
                <MenuItem value="African Bank Ltd">African Bank Ltd</MenuItem>
                <MenuItem value="Bidvest Bank Limited">Bidvest Bank Limited</MenuItem>
                <MenuItem value="Capitec Bank Ltd">Capitec Bank Ltd</MenuItem>
                <MenuItem value="First National Bank">First National Bank</MenuItem>
                <MenuItem value="FirstRand Bank Ltd">FirstRand Bank Ltd</MenuItem>
                <MenuItem value="Investec Bank Ltd">Investec Bank Ltd</MenuItem>
                <MenuItem value="Nedbank Group Limited">Nedbank Group Limited</MenuItem>
                <MenuItem value="Rand Merchant Bank">Rand Merchant Bank</MenuItem>
                <MenuItem value="Sasfin Bank Ltd">Sasfin Bank Ltd</MenuItem>
                <MenuItem value="Standard Bank of SA Ltd">Standard Bank of SA Ltd</MenuItem>
                <MenuItem value="Other">Other</MenuItem>
              </Select>

              {debitOrderBanking_Bank === 'Other' && (
                <Input
                  label="Other Bank Name"
                  {...register('otherDebitOrderBanking_BankName')}
                  error={!!errors?.otherDebitOrderBanking_BankName}
                  required
                  helperText={errors?.otherDebitOrderBanking_BankName?.message}
                />
              )}

              <Input
                label="Bank Account Number"
                {...register('debitOrderBanking_AccountNumber')}
                error={!!errors?.debitOrderBanking_AccountNumber}
                helperText={errors?.debitOrderBanking_AccountNumber?.message}
                required
              />

              <Input
                label="Swift"
                {...register('debitOrderBanking_Swift')}
                error={!!errors?.debitOrderBanking_Swift}
                helperText={errors?.debitOrderBanking_Swift?.message}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              {isBciApplication || isApexRihfOrQihf ? (
                <Select
                  required
                  name="debitOrderBanking_AccountType"
                  label="Bank Account Type"
                  control={control}
                  error={!!errors.debitOrderBanking_AccountType}
                  helperText={errors?.debitOrderBanking_AccountType?.message}
                >
                  <MenuItem value="Savings">Savings</MenuItem>
                  <MenuItem value="Current">Current</MenuItem>
                  <MenuItem value="Transmission">Transmission</MenuItem>
                </Select>
              ) : (
                <Select
                  required
                  name="debitOrderBanking_AccountType"
                  label="Bank Account Type"
                  control={control}
                  error={!!errors.debitOrderBanking_AccountType}
                  helperText={errors?.debitOrderBanking_AccountType?.message}
                >
                  <MenuItem value="Savings">Savings</MenuItem>
                  <MenuItem value="Cheque">Cheque</MenuItem>
                </Select>
              )}
              <Input
                label="Bank Branch"
                {...register('debitOrderBanking_BranchName')}
                error={!!errors?.debitOrderBanking_BranchName}
                helperText={errors?.debitOrderBanking_BranchName?.message}
                InputLabelProps={{
                  shrink: !!(
                    watchDebitOrderBanking_BranchName &&
                    watchDebitOrderBanking_BranchName.length > 0
                  ),
                }}
                required
              />

              <Input
                label="Bank Code"
                {...register('debitOrderBanking_BranchCode')}
                error={!!errors?.debitOrderBanking_BranchCode}
                helperText={errors?.debitOrderBanking_BranchCode?.message}
                InputLabelProps={{
                  shrink: !!(
                    watchDebitOrderBanking_BranchCode &&
                    watchDebitOrderBanking_BranchCode?.length > 0
                  ),
                }}
                required
              />

              <Input
                label="IBAN"
                {...register('debitOrderBanking_IBAN')}
                error={!!errors?.debitOrderBanking_IBAN}
                helperText={errors?.debitOrderBanking_IBAN?.message}
              />
            </Grid>
          </Grid>
        </>
      ) : null}

      <Grid item xs={12} md={12}>
        <NavButtons isLastStep={false} isFirstStep={false} index={0} />
      </Grid>
      <Box display={'inlineBlock'}>
        {objectToArray(errors).map((error, index) => (
          <Typography color={'error'} key={index}>
            {error.value.message}
          </Typography>
        ))}
      </Box>
    </Grid>
  );
}

export default BankingDetails;
